/**
 * TenancyPicker — searchable tenancy link for the work-report form (C2).
 *
 * Second thin wrapper over the shared ``AsyncCombobox`` (C0), alongside
 * ``TaskPicker``. It owns only the tenancy vocabulary — how to phrase a
 * tenancy for a human — and delegates debounce, paging, stale-response
 * protection, keyboard and theming to the base.
 *
 * Replaces a native <select> whose option text was essentially the bare
 * reference number: ``FAHN-MV-2026-000015`` is correct but tells nobody
 * which tenant, unit, room or period it is — and with internal moves the
 * same tenant legitimately has several tenancies that only differ in
 * exactly those fields.
 *
 * Rendered shape:
 *   Kamal Omran · Madrid / CL-004
 *   FAHN-MV-2026-000015 · 16.07.2026–31.03.2027 · Aktiv
 *
 * Server contract:
 *   GET /api/admin/tenancies?search=…&sort=picker_relevance&limit&skip
 *   GET /api/admin/tenancies/{id}   for hydrating the current link
 * Both return the same enriched shape, so no second normalisation.
 *
 * The payload it feeds back is unchanged: a bare ``tenancy_id`` string.
 */
import React from "react";

import AsyncCombobox from "../../../../components/ui/AsyncCombobox";
import {
  fetchAdminTenanciesPage,
  fetchAdminTenancyById,
} from "../../../../api/adminData";
import { formatDateOnly } from "../../../../utils/datetime";

const PAGE_SIZE = 20;

/** Mirrors the backend TenancyStatus enum (db/models.py:846-851) plus the
 * derived display_status the API already returns. Falls back to the raw
 * value so an unmapped status is still legible rather than blank. */
const STATUS_LABEL = {
  active: "Aktiv",
  reserved: "Reserviert",
  ended: "Beendet",
  cancelled: "Storniert",
};

export function statusLabel(tenancy) {
  const raw = tenancy?.display_status || tenancy?.status || null;
  if (!raw) return null;
  return STATUS_LABEL[raw] || raw;
}

/**
 * "Kamal Omran" — the flat field the API now provides, then e-mail, then
 * an honest placeholder. Never a UUID.
 * Exported for tests.
 */
export function tenantLabel(tenancy) {
  const name = (tenancy?.primary_tenant_display_name || "").trim();
  if (name) return name;
  const email = (tenancy?.primary_tenant_email || "").trim();
  if (email) return email;
  return "Mieter nicht verfügbar";
}

/**
 * "Madrid / CL-004", "Madrid", "CL-004" or null — segments that are
 * missing are dropped rather than rendered as an empty separator.
 */
export function placeLabel(tenancy) {
  const unit =
    (tenancy?.unit_title || "").trim() ||
    (tenancy?.unit_short_unit_id || "").trim();
  const room = (tenancy?.room_name || "").trim();
  return [unit, room].filter(Boolean).join(" / ") || null;
}

/**
 * "16.07.2026–31.03.2027" or "ab 16.07.2026" when the end is open.
 * Uses ``display_end_date`` (the API's own derived end) before falling
 * back to the raw ``move_out_date`` — no end date is invented here.
 */
export function periodLabel(tenancy) {
  const start = tenancy?.move_in_date;
  const end = tenancy?.display_end_date || tenancy?.move_out_date || null;
  if (!start && !end) return null;
  if (!start) return `bis ${formatDateOnly(end)}`;
  if (!end) return `ab ${formatDateOnly(start)}`;
  return `${formatDateOnly(start)}–${formatDateOnly(end)}`;
}

/** Primary line: "Mietername · Unit / Zimmer". Exported for tests. */
export function primaryLine(tenancy) {
  return [tenantLabel(tenancy), placeLabel(tenancy)].filter(Boolean).join(" · ");
}

/** Secondary line: "Referenz · Zeitraum · Status". Exported for tests. */
export function secondaryLine(tenancy) {
  return [
    (tenancy?.reference_number || "").trim() || null,
    periodLabel(tenancy),
    statusLabel(tenancy),
  ]
    .filter(Boolean)
    .join(" · ");
}

function isInactive(tenancy) {
  const s = tenancy?.status;
  return s === "ended" || s === "cancelled";
}

export default function TenancyPicker({ value, onChange, disabled = false }) {
  const [selected, setSelected] = React.useState(null);

  // Hydrate the CURRENT link by id (edit mode). Without this an already
  // linked tenancy that is ended/cancelled — or sorts past page 1 —
  // would render as "nothing selected" and a save could drop the link.
  React.useEffect(() => {
    if (!value) {
      setSelected(null);
      return undefined;
    }
    if (selected && String(selected.id) === String(value)) return undefined;
    let cancelled = false;
    fetchAdminTenancyById(value)
      .then((tenancy) => {
        if (!cancelled && tenancy) setSelected(tenancy);
      })
      .catch(() => {
        // A deleted or inaccessible tenancy must not break the form; the
        // link stays intact until the user changes it.
      });
    return () => {
      cancelled = true;
    };
  }, [value, selected]);

  // NOTE: ``fetchAdminTenanciesPage``, NOT ``fetchAdminTenancies`` — the
  // latter unwraps the response down to a bare item array, and the base
  // combobox reads ``{items, total}``. Passing the unwrapping variant
  // here made every page look empty (C2 regression).
  const fetchPage = React.useCallback(
    ({ query, offset, limit }) =>
      fetchAdminTenanciesPage({
        search: query || undefined,
        sort: "picker_relevance",
        // Cancelled tenancies stay findable — they are ranked last by
        // the server, and an existing link to one must remain
        // selectable when the user re-opens the picker.
        include_cancelled: true,
        limit,
        skip: offset,
      }),
    []
  );

  const handleChange = React.useCallback(
    (id, item) => {
      setSelected(item);
      onChange(id);
    },
    [onChange]
  );

  return (
    <AsyncCombobox
      label="Mietverhältnis (optional)"
      placeholder="— keine —"
      searchPlaceholder="Mietverhältnis nach Mieter, Unit, Zimmer oder Referenz suchen …"
      emptyText="Keine passenden Mietverhältnisse gefunden."
      errorText="Mietverhältnisse konnten nicht geladen werden."
      value={value}
      selectedItem={selected}
      onChange={handleChange}
      fetchPage={fetchPage}
      getItemId={(t) => t.id}
      getItemLabel={(t) => primaryLine(t)}
      pageSize={PAGE_SIZE}
      disabled={disabled}
      renderItem={(tenancy) => (
        <span className="block min-w-0">
          <span
            title={primaryLine(tenancy)}
            className={
              "block truncate text-[13px] " +
              (isInactive(tenancy)
                ? "font-normal text-muted-foreground"
                : "font-medium text-foreground")
            }
          >
            {primaryLine(tenancy)}
          </span>
          <span
            title={secondaryLine(tenancy)}
            className="block truncate text-[11px] text-muted-foreground"
          >
            {secondaryLine(tenancy)}
          </span>
        </span>
      )}
    />
  );
}
